import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import ReviewButton from "./ReviewButton";
import KimaruButton from "../buttons/KimaruButton";
import AgenticInteractionDrawer from "../agenticinteraction/AgenticInteractionDrawer";
import DeferButton from "../buttons/DeferButton";
import ContainerSuccessCard from "./ContainerSuccessCard";
import { getHeaderConfig } from "./containerCardConfigs";
import Tooltip from '@mui/material/Tooltip';

interface ContainerCardAlertProps {
    container: any;
    type: any;
    containerData: any;
    tableView?: any;
    onInventoryOptimizationPage?: any;
    selectedLens?: any;
    apiRecommendations?: {
        movement: any[];
        price: any[];
        totalCount: number;
        isCompleted: boolean;
    };
    onDataRefresh?: () => void;
}

function ContainerCardAlert({
    container,
    type,
    containerData,
    tableView,
    onInventoryOptimizationPage,
    selectedLens = "container",
    apiRecommendations,
    onDataRefresh
}: ContainerCardAlertProps) {
    const config: any = getHeaderConfig(container.type || "info");
    const containerId = containerData.container_id;
    const location = containerData.location;
    const navigate = useNavigate();
    const userEmail: any = useSelector((state: any) => state.auth?.user?.email);

    // Use passed-in API data instead of fetching
    const [totalRecommendationCount, setTotalRecommendationCount] = useState<number>(
        apiRecommendations?.totalCount || 0
    );
    const [isCompleted, setIsCompleted] = useState<boolean>(
        apiRecommendations?.isCompleted || false
    );
    const [agentOpen, setAgentOpen] = useState<boolean>(false);
    const [agentRecommendationId, setAgentRecommendationId] = useState<any>(null);
    const [isContainerDeferred, setIsContainerDeferred] = useState<boolean>(false);
    const [isDeferring, setIsDeferring] = useState<boolean>(false);

    // Update state when apiRecommendations prop changes
    useEffect(() => {
        if (apiRecommendations) {
            setTotalRecommendationCount(apiRecommendations.totalCount);
            setIsCompleted(apiRecommendations.isCompleted);
            setIsContainerDeferred(apiRecommendations.totalCount === 0);
        }
    }, [apiRecommendations]);

    const handleReviewClick = () => {
        navigate(`/container-recommendations/${containerId}`);
    };

    const handleViewSummaryClick = (summaryData: any) => {
        navigate(`/container-summary/${containerId}`, {
            state: {
                summaryData,
                containerData
            }
        });
    };

    const handleKimaruClick = async () => {
        // Use passed-in API data instead of fetching
        if (!apiRecommendations) {
            const fallback = containerData?.recommendations?.map((r: any) => r.rec_id).filter(Boolean) || null;
            setAgentRecommendationId(fallback && fallback.length > 0 ? fallback : null);
            setAgentOpen(true);
            return;
        }

        const movementIds = apiRecommendations.movement.map((r: any) => r.recommendation?.id).filter(Boolean);
        const priceIds = apiRecommendations.price.map((r: any) => r.recommendation?.id).filter(Boolean);
        const allIds = [...movementIds, ...priceIds];

        if (allIds.length > 0) {
            setAgentRecommendationId(allIds);
        } else {
            const fallback = containerData?.recommendations?.map((r: any) => r.recommendation?.id).filter(Boolean) || null;
            setAgentRecommendationId(fallback && fallback.length > 0 ? fallback : null);
        }

        setAgentOpen(true);
    };

    // Defer all recommendations for this container (both movement and price)
    const handleDeferContainerRecommendations = useCallback(async () => {
        if (!apiRecommendations) return;

        setIsDeferring(true);
        try {
            const movementIds = apiRecommendations.movement.map((r: any) => r.recommendation?.id).filter(Boolean);
            const priceIds = apiRecommendations.price.map((r: any) => r.recommendation?.id).filter(Boolean);

            const all = [
                ...movementIds.map((id: any) => ({ id, type: 'movement' })),
                ...priceIds.map((id: any) => ({ id, type: 'price' }))
            ];

            // Send defer requests in parallel
            const results = await Promise.all(all.map(async (rec) => {
                try {
                    const apiBase = rec.type === 'movement'
                        ? 'https://api.stag.kimaru.ai/api/v1/movement_recommendation'
                        : 'https://api.stag.kimaru.ai/api/v1/price_recommendation';

                    const resp = await fetch(`${apiBase}/defer_recommendation`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json', 'user-email': userEmail },
                        body: JSON.stringify({ recommendation_id: rec.id })
                    });

                    return { id: rec.id, ok: resp.ok };
                } catch (err) {
                    console.error('Error deferring rec', rec.id, err);
                    return { id: rec.id, ok: false };
                }
            }));

            const successCount = results.filter(r => r.ok).length;

            if (successCount > 0) {
                // If all were successful, mark container as deferred
                if (successCount === all.length) {
                    setIsContainerDeferred(true);
                    setTotalRecommendationCount(0);
                }
                // Trigger refresh on parent
                if (onDataRefresh) {
                    onDataRefresh();
                }
            }
        } catch (error) {
            console.error('Error deferring container recommendations:', error);
        } finally {
            setIsDeferring(false);
        }
    }, [apiRecommendations, containerId, userEmail, onDataRefresh]);

    // Alert type determination
    const isHeatWarningLocation = ["Miyazaki", "Osaka"].includes(location);
    const isSpoilageAlert = !isHeatWarningLocation;

    const getDynamicAlertType = () => {
        return isHeatWarningLocation ? "Heatwave Alert" : "Spoilage Alert";
    };

    const getMovementRecommendationCount = useCallback((): number => {
        if (!containerData?.recommendations || containerData.recommendations.length === 0) {
            return 0;
        }
        return containerData.recommendations.length;
    }, [containerData]);

    const truncateEnd = (text: string, maxLength: number): string => {
        if (text.length <= maxLength) return text;
        return `${text.slice(0, maxLength)}...`;
    };

    const getProductsFromContainerData = () => {
        if (!containerData?.recommendations || containerData.recommendations.length === 0) {
            return [];
        }

        const uniqueProducts: any[] = [];
        const seenSkus = new Set<any>();

        const daysCycle = [5, 10, 15];

        for (const rec of containerData.recommendations) {
            if (!seenSkus.has(rec.sku_id) && uniqueProducts.length < 3) {
                const idx = uniqueProducts.length;
                const dayValue = daysCycle[idx % daysCycle.length];

                seenSkus.add(rec.sku_id);

                uniqueProducts.push({
                    name: (
                        <Tooltip title={rec.item_name}>
                            <span>{truncateEnd(rec.item_name, 30)}</span>
                        </Tooltip>
                    ),
                    value: isHeatWarningLocation
                        ? `${Math.round(rec.sell_through_rate * 100)}%`
                        : `${dayValue} days`,
                    image: rec.item_image_url
                });
            }
        }

        return uniqueProducts;
    };

    const getLensSpecificHeader = () => {
        const alertName = isHeatWarningLocation ? "Heatwave Alert" : "Spoilage Alert";

        if (userEmail?.trim()?.toLowerCase() === "sam@kimaru.ai") {
            return {
                title: `Alert for Container ${containerId}`,
                breadcrumb: `Asia Pacific > Japan > ${location}`
            };
        }

        if (selectedLens === "product") {
            const lastProduct = containerData?.recommendations?.slice(-1)[0]?.item_name || "Product";
            return {
                title: `${alertName} → ${lastProduct}`,
                breadcrumb: "Dairy Products > Ice Cream"
            };
        }

        if (selectedLens === "store") {
            return {
                title: `${alertName} → Store #${containerId}`,
                breadcrumb: `${location} > Central District`
            };
        }

        return {
            title: `${alertName} → ${location} #${containerId}`,
            breadcrumb: `Asia Pacific > Japan > ${location}`
        };
    };

    const getSamMiddleSection = () => {
        // Default to component lens
        let lensType = selectedLens === "supplier" ? "supplier" : selectedLens === "location" ? "location" : "component";

        if (lensType === "supplier") {
            const suppliers = [
                "Shanghai Electronics Manufacturing Ltd.",
                "Beijing International Audio Bricks",
                "Shenzhen Component Technologies",
                "Guangzhou Precision Parts Co.",
                "Tokyo Advanced Materials Corp."
            ];

            const selectedSuppliers = [
                suppliers[containerId % suppliers.length],
                suppliers[(containerId + 1) % suppliers.length]
            ];

            return {
                title: "Suppliers Affected",
                items: selectedSuppliers.map((supplier, index) => ({
                    name: supplier,
                    value: `${15 + index * 5} days`,
                    image: null
                }))
            };
        } else if (lensType === "location") {
            return {
                title: "Region Affected by Incoming Typhoon",
                items: [{
                    name: "Port expects delays for next 5 days due to storm and increased shipping demand",
                    value: "",
                    image: null
                }]
            };
        } else {
            // Default component view - preserve original setup with images
            const productsFromData = getProductsFromContainerData();
            return {
                title: "Components Affected by Shipping Delay",
                items: productsFromData.length > 0 ? productsFromData.map((p: any) => ({
                    name: p.name,
                    value: p.value,
                    image: p.image
                })) : [
                    { name: "Emergency Supplies", value: "High Demand", image: "/assets/products/emergency-supplies.jpg" },
                    { name: "Water Bottles", value: "Critical", image: "/assets/products/water-bottles.jpg" },
                    { name: "Canned Food", value: "Moderate", image: "/assets/products/canned-food.jpg" }
                ]
            };
        }
    };

    if (isCompleted) {
        return (
            <ContainerSuccessCard
                containerData={containerData}
                onViewSummaryClick={handleViewSummaryClick}
                alertType={getDynamicAlertType()}
            />
        );
    }

    const getAlertConfig = () => {
        const headerConfig = getLensSpecificHeader();
        const productsFromData = getProductsFromContainerData();

        const getLensSpecificData = () => {
            if (userEmail?.trim()?.toLowerCase() === "sam@kimaru.ai") {
                return {
                    type: "Typhoon Alert",
                    data: {
                        weatherSource: "OpenWeather",
                        ExpectedImpact: "shipping disruptions",
                        duration: "Next 2 days",
                        confidence: "90%",
                        productsAffected: productsFromData.length > 0 ? productsFromData : [
                            { name: "Emergency Supplies", value: "High Demand", image: "/assets/products/emergency-supplies.jpg" },
                            { name: "Water Bottles", value: "Critical", image: "/assets/products/water-bottles.jpg" },
                            { name: "Canned Food", value: "Moderate", image: "/assets/products/canned-food.jpg" }
                        ]
                    },
                    productsLabel: "Products Affected by Typhoon:",
                    recommendationText: "prepare for high winds and heavy rain"
                };
            }

            if (selectedLens === "product") {
                return {
                    type: headerConfig.title,
                    breadcrumb: headerConfig.breadcrumb,
                    data: {
                        weatherSource: "OpenWeather",
                        ExpectedImpact: "shipping disruptions",
                        duration: "Next 2 days",
                        confidence: "90%",
                        productsAffected: [
                            { name: "Store #530", value: "340%", image: null },
                            { name: "Store #772", value: "240%", image: null },
                            { name: "Store #803", value: "220%", image: null }
                        ]
                    },
                    productsLabel: "Stores Affected by Demand Forecast:",
                    recommendationText: "prevent spoilage and minimize waste"
                };
            }

            const productsFallbackForHeat = [
                { name: "Premium Vanilla Ice Cream", value: "340%", image: "/assets/products/premium-vanilla-ice-cream.jpg" },
                { name: "Chocolate Fudge Bar", value: "240%", image: "/assets/products/chocolate-fudge-bar.jpg" },
                { name: "Strawberry Cream Bar", value: "220%", image: "/assets/products/strawberry-cream-bar.jpg" }
            ];

            const productsFallbackForSpoilage = [
                { name: "Milk Yogurt (100g)", value: "1", image: "/assets/products/oishii-milk-gallon.jpg" },
                { name: "Extra Thick Milk (900ml)", value: "2", image: "/assets/products/oishii-milk-half-gallon.jpg" },
                { name: "Gentle Stomach Milk (900ml)", value: "2", image: "/assets/products/mainichi-ko-half-gallon.jpg" }
            ];

            if (isHeatWarningLocation) {
                return {
                    type: headerConfig.title,
                    breadcrumb: headerConfig.breadcrumb,
                    data: {
                        weatherSource: "OpenWeather",
                        expectedTemperature: "40°C",
                        duration: "Next 4 days",
                        confidence: "95%",
                        productsAffected: productsFromData.length > 0 ? productsFromData : productsFallbackForHeat
                    },
                    productsLabel: "Products Affected by Demand Forecast:",
                    recommendationText: "hit forecasted sell-through target"
                };
            }

            return {
                type: headerConfig.title,
                breadcrumb: headerConfig.breadcrumb,
                data: {
                    expirySource: "SAP S/4HANA",
                    expiryDays: 1.5,
                    inStockDays: 30,
                    unitsOnHand: 115,
                    productsAffected: productsFromData.length > 0 ? productsFromData : productsFallbackForSpoilage
                },
                productsLabel: selectedLens === "product" ? "Stores Affected by Demand Forecast:" : "Products Affected by Expiry Days:",
                recommendationText: "prevent spoilage and minimize waste"
            };
        };

        return getLensSpecificData();
    };

    const alertConfig = getAlertConfig();
    const recommendationCount = totalRecommendationCount > 0 ? totalRecommendationCount : getMovementRecommendationCount();

    const spoilageData = {
        expiryDays: 2,
        inStockDays: 30,
        unitsOnHand: 115,
    };

    const isSam = userEmail?.trim()?.toLowerCase() === "sam@kimaru.ai";

    const leftSection: any = isSam
        ? {
            svg: "/assets/icons/Typhoon.svg",
            header: "Weather Source (OpenWeather)",
            details: [
                "Expected Impact: Shipping Disruptions",
                "Duration: Next 5 days",
                "Confidence: 92%",
            ],
        }
        : {
            svg: `/assets/icons/${isHeatWarningLocation ? 'day.svg' : 'spoilage.svg'}`,
            details: [
                isHeatWarningLocation ? `Expected Temp: ${alertConfig.data.expectedTemperature || alertConfig.data.duration}` : `Expiry (days): ${spoilageData.expiryDays}`,
                isHeatWarningLocation ? `Duration: ${alertConfig.data.duration}` : `In Stock (days): ${spoilageData.inStockDays}`,
                isHeatWarningLocation ? `Confidence: ${alertConfig.data.confidence}` : `Units on Hand: ${spoilageData.unitsOnHand}`,
            ],
        };

    const samMiddleSection = isSam ? getSamMiddleSection() : null;
    const middleSection = isSam ? samMiddleSection?.title : alertConfig.productsLabel;
    const rightSection = isSam ? `decrease order fulfillment risk by ${Math.floor(Math.random() * 10) + 90}%` : alertConfig.recommendationText;

    return (
        <div className="bg-white border border-slate-200 rounded-md shadow-sm">
            <div className="flex justify-between items-center h-[50px] px-4 py-2 bg-[#F8FAFC] border-b border-slate-200 rounded-t-md">
                <div className="flex-1">
                    <h2 className="text-xl font-semibold text-gray-900">
                        {alertConfig.type} → {location} #{containerId}
                    </h2>
                </div>
                <div className="text-sm text-gray-500">
                    {alertConfig.breadcrumb || `Asia Pacific > ${containerData?.country || 'Japan'} > ${location}`}
                </div>
            </div>

            <div className="flex flex-wrap justify-between items-start h-auto px-4 py-3">
                {/* Main Content Area */}
                {onInventoryOptimizationPage ? (
                    <div className="w-full">
                        <div className="flex items-start space-x-3">
                            <div className="flex-shrink-0">
                                <img
                                    src={leftSection.svg}
                                    alt="Alert icon"
                                    style={{ width: '24px', height: '24px' }}
                                />
                            </div>

                            <div className="flex-1">
                                <div className="mb-1" style={{ fontWeight: '600' }}>
                                    {isSam ? 'Weather Source (OpenWeather)' : (isSpoilageAlert ? 'Expiry Source (SAP S/4HANA)' : 'Weather Source (by OpenWeather)')}
                                </div>
                                <div className="space-y-1">
                                    {leftSection.details.map((detail: any, index: number) => (
                                        <div key={index} className="font-light">
                                            <span>{detail}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                ) : (
                    <>
                        {/* Left Section */}
                        <div className="flex-1 pr-6">
                            <div className="flex items-start space-x-3">
                                <div className="flex-shrink-0">
                                    <img
                                        src={leftSection.svg}
                                        alt="Alert icon"
                                        style={{ width: '24px', height: '24px' }}
                                    />
                                </div>

                                <div className="flex-1">
                                    <div className="mb-1" style={{ fontWeight: '600' }}>
                                        {isSam ? 'Weather Source (OpenWeather)' : (isSpoilageAlert ? 'Expiry Source (SAP S/4HANA)' : 'Weather Source (by OpenWeather)')}
                                    </div>
                                    <div className="space-y-1 text-sm">
                                        {leftSection.details.map((detail: any, index: number) => (
                                            <div key={index} className="flex items-center font-light">
                                                <span className="w-1/2 text-left">{detail.split(':')[0]}:</span>
                                                <span className="w-1/2 text-left">{detail.split(':')[1]}</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Middle Section */}
                        <div className="flex-[1] px-6">
                            <div className="mb-2" style={{ fontWeight: '600' }}>
                                {middleSection}
                            </div>
                            <div className="space-y-2">
                                {isSam ? (
                                    samMiddleSection?.items.map((item: any, index: number) => (
                                        <div key={index} className="flex items-center justify-between">
                                            <div className="flex items-center gap-2">
                                                {item.image && (
                                                    <img
                                                        src={item.image}
                                                        alt={item.name as any}
                                                        style={{ width: '16px', height: '16px', flexShrink: 0, aspectRatio: '1/1' }}
                                                        className="object-cover rounded"
                                                        onError={(e: any) => { e.target.style.display = 'none'; }}
                                                    />
                                                )}
                                                <span className="text-sm font-light">{item.name}</span>
                                            </div>
                                            {item.value && (
                                                <span className="text-sm font-light ml-4">{item.value}</span>
                                            )}
                                        </div>
                                    ))
                                ) : (
                                    alertConfig.data.productsAffected.map((product: any, index: number) => (
                                        <div key={index} className="flex items-center justify-between">
                                            <div className="flex items-center gap-2">
                                                {product.image && (
                                                    <img
                                                        src={product.image}
                                                        alt={product.name as any}
                                                        style={{
                                                            width: '16px',
                                                            height: '16px',
                                                            flexShrink: 0,
                                                            aspectRatio: '1/1'
                                                        }}
                                                        className="object-cover rounded"
                                                        onError={(e: any) => {
                                                            e.target.style.display = 'none';
                                                        }}
                                                    />
                                                )}
                                                <span className="text-sm font-light">{product.name}</span>
                                            </div>
                                            <span className="text-sm font-light ml-4">
                                                {product.value}
                                            </span>
                                        </div>
                                    ))
                                )}
                            </div>
                        </div>

                        {/* Right Section */}
                        <div className="flex-1 flex flex-col items-end space-y-3">
                            <div className="text-left mb-2">
                                Review {recommendationCount} recommendations to <br /> {rightSection}
                            </div>
                            <div className="flex space-x-3">
                                <ReviewButton
                                    icon={config.primaryButton.icon}
                                    number={recommendationCount}
                                    onClick={handleReviewClick}
                                />
                                <KimaruButton
                                    animated={true}
                                    onClick={handleKimaruClick}
                                    disabled={false}
                                />
                                <DeferButton
                                    isDeferred={isContainerDeferred}
                                    onClick={handleDeferContainerRecommendations}
                                    disabled={isDeferring || isContainerDeferred}
                                />
                            </div>
                        </div>
                    </>
                )}
            </div>

            {/* Agent drawer for container-level questions */}
            <AgenticInteractionDrawer
                open={agentOpen}
                onClose={() => { setAgentOpen(false); setAgentRecommendationId(null); }}
                recommendationId={agentRecommendationId}
                containerId={containerId}
                agentScope={'container'}
                logoPath="/kimaru-favicon.png"
                userImage={undefined}
                recommendation={undefined}
                recommendationType={undefined}
            />
        </div>
    );
}

export default ContainerCardAlert;